import React, { useEffect } from 'react'
import { StyleSheet, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  Easing,
} from 'react-native-reanimated'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'

const ROW_COUNT = 6

const SkeletonRow = ({ styles }: { styles: ReturnType<typeof getStyle> }): React.ReactElement => (
  <View style={styles.card}>
    <View style={styles.medallion} />
    <View style={styles.body}>
      <View style={[styles.line, styles.lineTitle]} />
      <View style={[styles.line, styles.lineText]} />
    </View>
  </View>
)

/**
 * First-load placeholder for the notifications list. A single shared opacity
 * pulse drives every row so the screen feels alive while the first page loads
 * (subsequent tab switches reuse the cached list and never show this).
 */
export const NotificationSkeleton = (): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const pulse = useSharedValue(0.5)

  useEffect(() => {
    pulse.value = withRepeat(
      withTiming(1, { duration: 800, easing: Easing.inOut(Easing.ease) }),
      -1,
      true,
    )
  }, [pulse])

  const animatedStyle = useAnimatedStyle(() => ({ opacity: pulse.value }))

  return (
    <Animated.View style={animatedStyle}>
      {Array.from({ length: ROW_COUNT }).map((_, index) => (
        <SkeletonRow key={index} styles={styles} />
      ))}
    </Animated.View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.md,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.grey05,
    },
    medallion: {
      width: 52,
      height: 52,
      borderRadius: 26,
      backgroundColor: theme.colors.grey10,
      marginRight: theme.spacing.md,
    },
    body: {
      flex: 1,
      gap: theme.spacing.sm,
    },
    line: {
      height: 12,
      borderRadius: theme.border.extraSmall,
      backgroundColor: theme.colors.grey10,
    },
    lineTitle: {
      width: '55%',
    },
    lineText: {
      width: '80%',
    },
  })
